import { useTranslation } from 'react-i18next';
import type { FieldError as RhfFieldError } from 'react-hook-form';
import { FieldDescription, FieldError } from '@/shared/components/ui/field';
import { AiBadge } from '@/shared/components/custom/ai-badge/AiBadge';

interface ConvertToTaskFieldProps {
  label: string;
  /** Renders the red asterisk the design puts on required fields. */
  required?: boolean;
  /** Translated AI caption — omit when the field was not AI-filled. */
  aiLabel?: string;
  /** Muted note under the control, e.g. why the AI picked this value. */
  hint?: string;
  /** Zod message — an i18n key, translated here so the UI never shows the raw key. */
  error?: RhfFieldError;
  htmlFor?: string;
  children: React.ReactNode;
}

/**
 * One row of the convert-to-task dialog: label, optional AI badge, control
 * and hint. Exists so every row stacks identically — validation errors are
 * rendered by the shared form-field component that wraps the control.
 */
export function ConvertToTaskField({
  label,
  required = false,
  aiLabel,
  hint,
  error,
  htmlFor,
  children,
}: ConvertToTaskFieldProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <label htmlFor={htmlFor} className="text-sm font-medium">
          {label}
          {required && (
            <span className="text-destructive" aria-hidden>
              *
            </span>
          )}
        </label>
        {aiLabel && <AiBadge size="sm" label={aiLabel} className="border-none" />}
      </div>

      {children}

      {error?.message && <FieldError className="text-xs">{t(error.message)}</FieldError>}
      {hint && <FieldDescription className="text-xs">{hint}</FieldDescription>}
    </div>
  );
}
